import { useCallback, useEffect, useState } from "react";

import { FileType, SocialMediaData } from "../../../models";
import { MapProjectCoordinates } from "../../../models/shared/mapProjectCoordinates";
import { searchProjects } from "../../../service/publicquery";
import { getDeveloperDetails } from "../../../service/query";
import { Status } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { DetailsMapConstants } from "../constants";

interface UsePublicDeveloperDetailsPreviewTemplateReturnData {
  dataIsLoading: boolean;
  developerUuid: string | undefined | null;
  developerDetails: DeveloperDetails | undefined;
  developerSocialMedia: SocialMediaData | undefined;
  projectsCoordinates: MapProjectCoordinates[] | undefined;
}

export interface DeveloperDetails {
  displayName: string | null;
  logo: string | null;
  websiteUrl: string | null;
}

export const usePublicDeveloperDetailsPreviewTemplate = (): UsePublicDeveloperDetailsPreviewTemplateReturnData => {
  const { currentDeveloperUuid } = useAuth();

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [projectsCoordinates, setProjectsCoordinates] = useState<MapProjectCoordinates[] | undefined>();
  const [developerDetails, setDeveloperDetails] = useState<DeveloperDetails | undefined>();
  const [developerSocialMedia, setDeveloperSocialMedia] = useState<SocialMediaData | undefined>();

  const fetchData = useCallback(async (): Promise<void> => {
    if (currentDeveloperUuid) {
      const res = await getDeveloperDetails({
        developerUuid: currentDeveloperUuid,
      });

      if (res.status === Status.Success && res.data) {
        const logo = res.data.files.find((f) => f.type === FileType.LargeLogo)?.file.url || null;

        setDeveloperDetails({
          displayName: res.data.displayName,
          logo,
          websiteUrl: res.data.websiteUrl,
        });

        setDeveloperSocialMedia({
          twitterUsername: res.data.socialMediaTwitterUsername,
          linkedinURL: res.data.socialMediaLinkedInUrl,
          instagramUsername: res.data.socialMediaInstagramUsername,
          youtubeChannel: res.data.socialMediaYoutubeChannel,
          facebookURL: res.data.socialMediaFacebookUrl,
        });
      }

      const projectsRes = await searchProjects({
        paging: {
          limit: DetailsMapConstants.MAXIMUM_MARKERS,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: {
          results: {
            developer: {
              uuid: { operator: "eq", value: currentDeveloperUuid },
            },
          },
        },
      });

      if (projectsRes.status === Status.Success && projectsRes.data) {
        setProjectsCoordinates(
          projectsRes.data.results
            .filter((project) => project.locationCoordinates !== null)
            .flatMap((project) => ({ coordinate: project.locationCoordinates as string }))
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchData().then(() => setDataIsLoading(false));
  }, [fetchData]);

  return {
    dataIsLoading,
    developerUuid: currentDeveloperUuid,
    developerDetails,
    developerSocialMedia,
    projectsCoordinates,
  };
};
